<script setup lang="ts">
import { computed, ref, toRefs } from "vue";
import { isLorTodo } from "@/components/MyStudents/Submissions/letterOfRecommendation";
import SubmissionCurrent from "./components/SubmissionCurrent.vue";
import SubmissionPreviewModal from "./components/SubmissionPreviewModal/index.vue";
import SubmissionsHistory from "./components/SubmissionsHistory.vue";
import type { SubmissionFileOverride, SubmissionReadOverride, SubmissionsInformationProps } from "./types";

const props = defineProps<SubmissionsInformationProps>();

const { todoSubmission, participantEmail } = toRefs(props);

const isTodoArchived = computed(() => !!todoSubmission.value?.archived_at);
const titleSuffix = computed(() => (isLorTodo(todoSubmission.value?.todo_id) ? " - Recommendation Request" : ""));

const orderedSubmissions = computed<SubmissionReadOverride[]>(() => {
  return [...(todoSubmission.value?.submissions ?? [])].sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return dateB - dateA;
  }) as SubmissionReadOverride[];
});
const recentSubmission = computed(() => orderedSubmissions.value[0] ?? null);
const historySubmissions = computed(() => orderedSubmissions.value.slice(1));

const previewVisible = ref<boolean>(false);
const previewSubmission = ref<SubmissionReadOverride | undefined>(undefined);
const previewFile = ref<SubmissionFileOverride | undefined>(undefined);

const openPreview = (submission: SubmissionReadOverride, file: SubmissionFileOverride): void => {
  previewSubmission.value = submission;
  previewFile.value = file;
  previewVisible.value = true;
};
</script>

<template>
  <div v-if="orderedSubmissions.length" class="flex flex-col mt-6 gap-y-4" data-testid="my-students-information-section">
    <SubmissionCurrent
      :submission="recentSubmission"
      :is-todo-archived="isTodoArchived"
      :title-suffix="titleSuffix"
      @open-preview="openPreview"
    />
    <SubmissionsHistory :history="historySubmissions" @open-preview="openPreview"/>
    <SubmissionPreviewModal
      v-model:visible="previewVisible"
      :file="previewFile"
      :submission-id="previewSubmission?.id"
      :submitted-by="participantEmail"
      :submitted-at="previewSubmission?.created_at"
    />
  </div>
  <div
    v-else
    data-testid="my-students-submission-not-found"
    class="shadow-md rounded-lg p-4 mt-6 w-full"
  >
    <span class="font-semibold text-indigo-base">No Submission found!</span>
  </div>
</template>
